import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IDIncomeVerificationStatus, VerificationType } from '@common/entities';
import { setIDVerification, setIncomeVerification } from 'state/slices/idIncomeVerificationSlice';

import { useFetchVerificationStatus } from './useFetchVerificationStatus';
import useHandleVerificationFailedWorkFlow from './useHandleVerificationFailedWorkFlow';
import { useFetchVerificationNotification } from './useTriggerNotification';
import { isVerificationCompleted } from './verificationHelpers';

interface PollState {
  isPolling: boolean;
  attemptCount: number;
  lastStatus: any;
  error: string | null;
}

/**
 * Custom hook for polling verification status
 *
 * Features:
 * - Polls every 30 seconds for maximum 5 attempts
 * - Only polls if status is 'initiated', 'reinitiated', or 'inprogress'
 * - Stops polling if both statuses are null
 * - Automatically stops when verification is complete
 *
 * @returns Polling state and control functions
 */
export const usePollVerificationStatus = () => {
  const { fetchVerificationStatus } = useFetchVerificationStatus();
  const { fetchVerificationNotifications } = useFetchVerificationNotification();
  const { handleVerificationFailedWorkFlow } = useHandleVerificationFailedWorkFlow();

  const dispatch = useDispatch();

  // State management
  const [state, setState] = useState<PollState>({
    isPolling: false,
    attemptCount: 0,
    lastStatus: null,
    error: null,
  });

  // Timer reference (doesn't cause re-renders)
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // AbortController for cancelling in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Flag to prevent concurrent poll executions
  const isPollingRef = useRef<boolean>(false);

  // Configuration constants
  const MAX_ATTEMPTS = parseInt(process.env.ID_INCOME_VERIFICATION_POLLING_MAX_ATTEMPTS ?? '5', 10);
  const POLL_INTERVAL_MS = parseInt(
    process.env.ID_INCOME_VERIFICATION_DELAY_IN_STATUS_CHECK ?? '30000',
    10,
  ); // 30 seconds

  /**
   * Clear the polling timer and abort any in-flight request
   */
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  /**
   * Check if we should continue polling based on status
   */
  const shouldContinuePolling = useCallback((status: any): boolean => {
    if (!status) return false;

    const idStatus = status.idVerification?.status;
    const incomeStatus = status.incomeVerification?.status;

    // Don't poll if both are null
    if (idStatus === null && incomeStatus === null) {
      return false;
    }

    // Only poll if status is in progress states
    const pollableStatuses = new Set([IDIncomeVerificationStatus.InProgress]);

    const shouldPollId = idStatus && pollableStatuses.has(idStatus);
    const shouldPollIncome = incomeStatus && pollableStatuses.has(incomeStatus);

    return !!(shouldPollId || shouldPollIncome);
  }, []);

  /**
   * Main polling function
   * Fetches status and schedules next poll if needed
   * Uses AbortController to prevent concurrent requests
   */
  const poll = useCallback(async () => {
    // Prevent concurrent poll executions
    if (isPollingRef.current) return;

    isPollingRef.current = true;

    // Cancel any existing request
    if (abortControllerRef.current) abortControllerRef.current.abort();

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      const status = await fetchVerificationStatus(signal);

      // Check if the request was aborted
      if (signal.aborted) return;

      setState((prevState) => {
        const newAttemptCount = prevState.attemptCount + 1;

        // Check if we should continue polling
        const shouldContinue = shouldContinuePolling(status) && newAttemptCount < MAX_ATTEMPTS;

        if (shouldContinue) {
          // Schedule next poll
          timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          // Stop polling
          clearTimer();

          checkNotifications(status);
          if (status?.idVerification?.status) {
            dispatch(setIDVerification({ status: status.idVerification.status }));
            handleVerificationFailedWorkFlow(
              VerificationType.IdentityVerification,
              status.idVerification.status,
            );
          }

          if (status?.incomeVerification?.status) {
            dispatch(setIncomeVerification({ status: status.incomeVerification.status }));
            handleVerificationFailedWorkFlow(
              VerificationType.IncomeVerification,
              status.incomeVerification.status,
            );
          }
        }

        return {
          isPolling: shouldContinue,
          attemptCount: newAttemptCount,
          lastStatus: status,
          error: null,
        };
      });
    } catch (error: any) {
      // Ignore aborted requests
      if (error?.name === 'AbortError' || error?.name === 'CanceledError') {
        return;
      }

      // Handle error and stop polling
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch verification status';

      setState((prevState) => ({
        ...prevState,
        isPolling: false,
        error: errorMessage,
      }));

      clearTimer();
    } finally {
      isPollingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchVerificationStatus, shouldContinuePolling, clearTimer, dispatch]);

  /**
   * Start polling
   * Resets state and begins polling immediately
   */
  const startPolling = useCallback(async () => {
    // Reset state
    setState({
      isPolling: true,
      attemptCount: 0,
      lastStatus: null,
      error: null,
    });

    // Clear any existing timer
    clearTimer();

    // Start polling immediately
    await poll();
  }, [clearTimer, poll]);

  /**
   * Stop polling manually
   */
  const stopPolling = useCallback(() => {
    clearTimer();
    setState((prevState) => ({
      ...prevState,
      isPolling: false,
    }));
  }, [clearTimer]);

  /**
   * Cleanup on unmount
   * Ensures no memory leaks from timers
   */
  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  const checkNotifications = useCallback(
    async (status: any) => {
      const checkId = isVerificationCompleted(status?.idVerification?.status);
      const checkIncome = isVerificationCompleted(status?.incomeVerification?.status);
      await fetchVerificationNotifications(checkId, checkIncome);
    },
    [fetchVerificationNotifications],
  );

  return {
    // State
    isPolling: state.isPolling,
    attemptCount: state.attemptCount,
    lastStatus: state.lastStatus,
    error: state.error,

    // Actions
    startPolling,
    stopPolling,
  };
};
